import React, { useCallback, useEffect, useRef } from "react";
import { GoogleMap, MarkerF } from "@react-google-maps/api";
import { SNAZZY_MAP_STYLES } from "@/constants";
import { Controller, useFormContext } from "react-hook-form";
import useGoogleMap from "@/hooks/use-google-hook";

const GoogleSearch = ({ onChange, value, isLoaded, id, ...rest }) => {
  const ref = useRef(null);

  const handleScriptLoad = (ref) => {
    const autoComplete = new window.google.maps.places.Autocomplete(
      ref.current,
      {
        types: ["geocode"],
      }
    );
    autoComplete.addListener("place_changed", () =>
      handlePlaceSelect(autoComplete)
    );
  };

  const handlePlaceSelect = (autoComplete) => {
    const addressObject = autoComplete.getPlace();
    const addressComponents = addressObject?.address_components || [];

    const getComponent = (type) =>
      addressComponents.find((component) => component.types.includes(type))
        ?.long_name || "";

    const addressDetails = {
      address: addressObject?.formatted_address || "",
      city: getComponent("locality"),
      state: getComponent("administrative_area_level_1"),
      country: getComponent("country"),
      zipcode: getComponent("postal_code"),
      latitude: addressObject?.geometry?.location?.lat(),
      longitude: addressObject?.geometry?.location?.lng(),
    };

    onChange(addressDetails);
  };

  useEffect(() => {
    if (isLoaded) {
      handleScriptLoad(ref);
      setTimeout(() => {
        if (ref.current) {
          ref.current.setAttribute("autocomplete", "new-password");
        }
      }, 1000);
    }
  }, [isLoaded]);

  return (
    <div className="w-full">
      <input
        id={id ?? "address"}
        className="w-full border border-gray-300 rounded-md px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search for business address..."
        autoComplete="new-password"
        {...rest}
      />
    </div>
  );
};

const LocationForm = () => {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = useFormContext();

  const latitude = watch("latitude");
  const longitude = watch("longitude");
  const { isLoaded, onLoad, onUnmount } = useGoogleMap();

  const formatPrecision = (value, precision = 6) => {
    return parseFloat(parseFloat(value).toFixed(precision));
  };

  const findAddress = useCallback(() => {
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);

    if (isNaN(lat) || isNaN(lng)) {
      console.error("Invalid latitude or longitude");
      return;
    }

    const latLng = new window.google.maps.LatLng(lat, lng);
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === "OK" && results[0]) {
        const addressObject = results[0];
        const addressComponents = addressObject?.address_components || [];

        const getComponent = (type) =>
          addressComponents.find((component) => component.types.includes(type))
            ?.long_name || "";

        setValue("address", addressObject.formatted_address);
        setValue("city", getComponent("locality"));
        setValue("state", getComponent("administrative_area_level_1"));
        setValue("country", getComponent("country"));
        setValue("zipcode", getComponent("postal_code"));

        // Trigger validation for all fields
        trigger(["address", "city", "state", "country", "zipcode"]);
      } else {
        console.error("Geocoder failed due to: ", status);
      }
    });
  }, [latitude, longitude, setValue, trigger]);

  const onDragEnd = useCallback(
    (e) => {
      const newLat = formatPrecision(e.latLng.lat());
      const newLng = formatPrecision(e.latLng.lng());

      setValue("latitude", newLat);
      setValue("longitude", newLng);
      trigger(["latitude", "longitude"]);
    },
    [setValue, trigger]
  );

  if (!isLoaded)
    return (
      <div className="flex justify-center items-center h-64 bg-gray-100 rounded-lg">
        <div className="text-center">
          <i className="fa fa-spinner fa-spin text-2xl text-blue-500 mr-2"></i>
          <p className="text-gray-600 mt-2">Loading map...</p>
        </div>
      </div>
    );

  return (
    <div className="bg-white  rounded-xl shadow-sm  space-y-6">
      <div className="border-b pb-4">
        <h3 className="text-xl font-semibold text-gray-800 flex items-center">
          <i className="fa fa-map-marker-alt text-blue-500 mr-2"></i>
          Business Location
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Address Search */}
        <div className="md:col-span-2">
          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="fa fa-search text-blue-500 mr-2"></i>
              Search Address
              <span className="text-red-500 ml-1">*</span>
            </label>
            <p className="text-sm text-gray-500 !mb-3">
              Search for your business address to automatically fill location
              details.
            </p>
          </div>

          <Controller
            name="address"
            control={control}
            render={({ field: { onChange, value } }) => (
              <div className="flex flex-col gap-1">
                <GoogleSearch
                  isLoaded={isLoaded}
                  onChange={(address) => {
                    if (typeof address === "string") {
                      onChange(address);
                    } else if (address?.address) {
                      // Set all form values from the address object
                      onChange(address.address);
                      setValue("city", address.city, { shouldValidate: true });
                      setValue("state", address.state, {
                        shouldValidate: true,
                      });
                      setValue("country", address.country, {
                        shouldValidate: true,
                      });
                      setValue("zipcode", address.zipcode, {
                        shouldValidate: true,
                      });
                      setValue("latitude", address.latitude, {
                        shouldValidate: true,
                      });
                      setValue("longitude", address.longitude, {
                        shouldValidate: true,
                      });
                    }
                  }}
                  value={value}
                />
                {errors.address && (
                  <span className="text-red-500 text-sm mt-2 flex items-center">
                    <i className="fa fa-exclamation-circle mr-1"></i>
                    {errors.address.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        {/* Apartment/Suite */}
        <div>
          <label className="block text-sm font-medium text-gray-700 !mb-2">
            <i className="fa fa-home text-blue-500 mr-2"></i>
            Apartment/Suite (optional)
          </label>
          <Controller
            name="apt_suite"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <input
                  {...field}
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. Suite 405"
                />
              </div>
            )}
          />
        </div>

        {/* City */}
        <div>
          <label className="block text-sm font-medium text-gray-700 !mb-2">
            <i className="fa fa-city text-blue-500 mr-2"></i>
            City
            <span className="text-red-500 ml-1">*</span>
          </label>
          <Controller
            name="city"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <input
                  {...field}
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. New York"
                />
                {errors.city && (
                  <span className="text-red-500 text-sm mt-2 flex items-center">
                    <i className="fa fa-exclamation-circle mr-1"></i>
                    {errors.city.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        {/* State */}
        <div>
          <label className="block text-sm font-medium text-gray-700 !mb-2">
            <i className="fa fa-flag text-blue-500 mr-2"></i>
            State
            <span className="text-red-500 ml-1">*</span>
          </label>
          <Controller
            name="state"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <input
                  {...field}
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. New York"
                />
                {errors.state && (
                  <span className="text-red-500 text-sm mt-2 flex items-center">
                    <i className="fa fa-exclamation-circle mr-1"></i>
                    {errors.state.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        {/* ZIP / Postal Code */}
        <div>
          <label className="block text-sm font-medium text-gray-700 !mb-2">
            <i className="fa fa-envelope text-blue-500 mr-2"></i>
            ZIP / Postal Code
            <span className="text-red-500 ml-1">*</span>
          </label>
          <Controller
            name="zipcode"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <input
                  {...field}
                  onChange={(e) => {
                    setValue("zipcode", e.target.value);
                    trigger("zipcode");
                  }}
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. 10001"
                />
                {errors.zipcode && (
                  <span className="text-red-500 text-sm mt-2 flex items-center">
                    <i className="fa fa-exclamation-circle mr-1"></i>
                    {errors.zipcode.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        {/* Area */}
        <div>
          <label className="block text-sm font-medium text-gray-700 !mb-2">
            <i className="fa fa-map text-blue-500 mr-2"></i>
            Area (optional)
          </label>
          <Controller
            name="area"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <input
                  {...field}
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. Manhattan"
                />
              </div>
            )}
          />
        </div>

        {/* Country */}
        <div>
          <label className="block text-sm font-medium text-gray-700 !mb-2">
            <i className="fa fa-globe text-blue-500 mr-2"></i>
            Country
            <span className="text-red-500 ml-1">*</span>
          </label>
          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <input
                  {...field}
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. United States"
                />
                {errors.country && (
                  <span className="text-red-500 text-sm mt-2 flex items-center">
                    <i className="fa fa-exclamation-circle mr-1"></i>
                    {errors.country.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>
      </div>

      {/* Map Section */}
      <div className="mt-6">
        <div className=" !mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <i className="fa fa-map-pin text-blue-500 mr-2"></i>
            Location Map
          </label>
          <p className="text-sm text-gray-500 !mb-0">
            Drag the marker to adjust your precise location. Coordinates will
            update automatically.
          </p>
        </div>

        <div className="w-full h-[400px] rounded-lg overflow-hidden border border-gray-300 shadow-sm">
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={{ lat: +latitude || 39.5, lng: +longitude || -100.0 }}
            zoom={+latitude && +longitude ? 15 : 3.6}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={{
              styles: SNAZZY_MAP_STYLES,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
            }}
          >
            <MarkerF
              draggable
              onDragEnd={onDragEnd}
              position={{ lat: +latitude || 39.5, lng: +longitude || -100.0 }}
              icon={{
                url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
                scaledSize: new window.google.maps.Size(40, 40),
              }}
            />
          </GoogleMap>
        </div>
      </div>

      {/* Coordinates Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        {/* Latitude */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <i className="fa fa-latitude text-blue-500 mr-2"></i>
            Latitude
            <span className="text-red-500 ml-1">*</span>
          </label>
          <Controller
            name="latitude"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <input
                  {...field}
                  type="number"
                  min="-90"
                  max="90"
                  step="any"
                  className="w-full border border-gray-300 rounded-md px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Latitude"
                />
                {errors.latitude && (
                  <span className="text-red-500 text-sm mt-2 flex items-center">
                    <i className="fa fa-exclamation-circle mr-1"></i>
                    {errors.latitude.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        {/* Longitude */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <i className="fa fa-longitude text-blue-500 mr-2"></i>
            Longitude
            <span className="text-red-500 ml-1">*</span>
          </label>
          <Controller
            name="longitude"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <input
                  {...field}
                  type="number"
                  min="-180"
                  max="180"
                  step="any"
                  className="w-full border border-gray-300 rounded-md px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Longitude"
                />
                {errors.longitude && (
                  <span className="text-red-500 text-sm mt-2 flex items-center">
                    <i className="fa fa-exclamation-circle mr-1"></i>
                    {errors.longitude.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        {/* Find Address Button */}
        <div className="flex items-end">
          <button
            onClick={findAddress}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white font-medium py-3 px-4 rounded-md shadow-md transition-all duration-300 h-[50px]"
          >
            <i className="fa fa-map-marked-alt mr-2"></i>
            Find Address
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationForm;
